/** @jsxImportSource @emotion/react */
import { useRef, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import { SubTitleLayout, SubTitleGroup, ControlBtnGroup } from 'components/layouts/ContentLayout';
import CustomDialog from 'components/modals/common/CustomDialog';
import { IconButton } from 'components/buttons/IconSVG';
import { useMessageBar } from 'hooks/useMessageBar';
import { useLoading } from 'components/process/Loading';
import CustomGrid from 'components/grids/CustomGrid';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import {
  SearchBox,
  SearchBoxRow,
  InputBox,
  SearchRows,
  SearchCols,
} from 'components/layouts/SearchBox';
import { findMdCustomerList, saveMdCustomer } from 'apis/md/MdCustomer';
import { MdCustomer, MdCustomerCondition } from 'models/md/MdCustomer';
import { CommonUtil } from 'utils/CommonUtil';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import { CrudCode } from 'models/common/Edit';
import { CellType } from '@grapecity/wijmo.grid';
import { useCommonModal } from 'hooks/useCommonModal';

type Props = {
  open: boolean;
  close: () => void;
};

/**
 * 고객사 등록 팝업
 */
const CustomerRegistPopup = ({ open, close, ...props }: Props) => {
  console.log('@@@');
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const { openLoading } = useLoading();
  const { openCommonModal } = useCommonModal();

  const initCondition = {
    custNm: '',
  };

  const gridRef = useRef<any>();
  const [condition, setCondition] = useState<MdCustomerCondition>(initCondition);
  const [rowData, setRowData] = useState<MdCustomer[]>([]);

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'crudKey',
        header: String(t('com.label.상태', '상태')),
        minWidth: 43,
        width: 43,
        align: 'center',
        isReadOnly: true,
        cellTemplate: GridStatusCellTemplate,
      },
      {
        binding: 'custId',
        header: String(t('com.label.고객사ID', '고객사ID')),
        minWidth: 70,
        width: 120,
        align: 'left',
      },
      {
        binding: 'custNm',
        header: String(t('com.label.고객사명', '고객사명')),
        minWidth: 70,
        width: '*',
        align: 'left',
      },
      {
        binding: 'dataUpdUserNm',
        header: String(t('com.label.수정자', '수정자')),
        minWidth: 70,
        width: 120,
        align: 'left',
      },
      {
        binding: 'dataUpdDtm',
        header: String(t('com.label.수정일자', '수정일자')),
        minWidth: 70,
        width: 120,
        align: 'left',
      },
    ];
  }, [rowData]);

  const onInitialized = (grid) => {
    gridRef.current = grid;

    grid.formatItem.addHandler((s, e) => {
      if (CellType.Cell === e.panel.cellType) {
        const binding = e.panel.columns[e.col].binding;
        const item = e.panel.rows[e.row].dataItem;

        // 추가한 행의 custNm만 입력 UI 적용
        if (item.crudKey === CrudCode.CREATE && binding === 'custNm') {
          e.cell.ariaReadOnly = false;
        } else {
          e.cell.ariaReadOnly = true;
        }
      }
    });
  };

  const onBeginningEdit = (grid, e) => {
    const binding = grid.columns[e.col].binding;
    const item = grid.rows[e.row].dataItem;

    // 추가한 행의 custNm만 입력 가능
    if (item.crudKey === CrudCode.CREATE && binding === 'custNm') {
      e.cancel = false;
    } else {
      e.cancel = true;
    }
  };

  const handleOnChange = (e) => {
    setCondition((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const btnSearch = () => {
    // search
    openLoading(true);
    findMdCustomerList(condition)
      .then((res) => {
        setRowData(res);
      })
      .catch((err) => {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      })
      .finally(() => openLoading(false));
  };

  const btnAddRow = () => {
    const newRow = {
      crudKey: CrudCode.CREATE,
    } as MdCustomer;

    setRowData([newRow, ...rowData]);
  };

  const btnDelRow = () => {
    const selectedIds = gridRef.current.selectedItems?.map((o: MdCustomer) => o.custId);
    if (selectedIds?.length === 0) {
      openMessageBar({
        type: 'error',
        content: t('com.msg.선택된 데이터가 없습니다.', '선택된 데이터가 없습니다.'),
      });
    }

    setRowData((prev) =>
      prev.map((o) => {
        if (selectedIds.includes(o.custId)) return { ...o, crudKey: CrudCode.DELETE };
        else return { ...o };
      })
    );
  };

  const handleDownloadExcel = () => {
    CommonUtil.exportWijmoExcelGrid(
      gridRef.current,
      t('setup.label.고객사 목록', '고객사 목록'),
      t('setup.label.고객사 목록', '고객사 목록')
    );
  };

  const btnSave = () => {
    // save
    openCommonModal({
      modalType: 'confirm',
      content: t(`com.msg.변경사항을 저장하시겠습니까?`, `변경사항을 저장하시겠습니까?`),
      yesCallback: async () => {
        openLoading(true);
        saveMdCustomer(rowData)
          .then((res) => {
            if (res.successOrNot === 'Y') {
              openMessageBar({
                type: 'confirm',
                content: t('com.label.저장되었습니다.', '저장되었습니다.'),
              });
              btnSearch();
            } else {
              openMessageBar({
                type: 'error',
                content: t('com.label.저장에 실패하였습니다.', '저장에 실패하였습니다.'),
              });
            }
          })
          .catch((err) => {
            openMessageBar({
              type: 'error',
              content: t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
            });
          })
          .finally(() => openLoading(false));
      },
    });
  };

  useEffect(() => {
    btnSearch();
  }, []);

  const dialogButtons = [
    <Button
      key={'confirm'}
      css={IconButton.button}
      className="confirm"
      onClick={btnSave}
      disableRipple
    >
      {t('com.button.저장', '저장')}
    </Button>,
  ];
  return (
    <CustomDialog
      title={String(t('setup.label.고객사 등록', '고객사 등록'))}
      size={'md'}
      open={open}
      onClose={close}
      onCancel={close}
      buttons={dialogButtons}
    >
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows className="twoCol">
              <SearchCols>
                <label>{t('com.label.고객사명', '고객사명')}</label>
                <CustomInputWithSearch
                  name="custNm"
                  value={condition?.custNm}
                  onChange={handleOnChange}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchBoxButtons
            defaultCondition={initCondition}
            setReset={setCondition}
            onSearch={btnSearch}
          />
        </SearchBoxRow>
      </SearchBox>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.고객사', '고객사')}</h3>
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button css={IconButton.button} className="addRow" onClick={btnAddRow} disableRipple>
            {t('com.label.행추가', '행추가')}
          </Button>
          <Button css={IconButton.button} className="delRow" onClick={btnDelRow} disableRipple>
            {t('com.label.행삭제', '행삭제')}
          </Button>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button css={IconButton.button} className="refresh" onClick={btnSearch}>
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={500}
        isFilter={false}
        allowPinning={false}
        align="center"
        initialized={onInitialized}
        beginningEdit={onBeginningEdit}
      />
    </CustomDialog>
  );
};

export default CustomerRegistPopup;
